/**
 * RoleBadge Component - Badge wyświetlający rolę użytkownika
 * wojskaspecjalne.pl
 */

import React from 'react';
import { Crown, Shield, User } from 'lucide-react';
import { ROLE_LABELS, ROLE_COLORS, getRoleBadgeStyle } from '../lib/roleService';
import type { RoleName } from '../types/database.types';

interface RoleBadgeProps {
  role: RoleName;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Badge wyświetlający rolę użytkownika z odpowiednim stylem
 * 
 * @example
 * <RoleBadge role="admin" />
 * <RoleBadge role="moderator" size="lg" />
 */
export function RoleBadge({ role, className = '', size = 'md' }: RoleBadgeProps) {
  const baseStyle = getRoleBadgeStyle(role);

  const sizeStyles = {
    sm: {
      fontSize: '0.65rem',
      padding: '0.15rem 0.5rem',
    },
    md: {
      fontSize: '0.75rem',
      padding: '0.25rem 0.75rem',
    },
    lg: {
      fontSize: '0.875rem',
      padding: '0.35rem 1rem',
    },
  };

  const style = {
    ...baseStyle,
    ...sizeStyles[size],
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
  };

  // Ikony dla poszczególnych ról
  const IconComponent = {
    admin: Crown,
    moderator: Shield,
    user: User,
  }[role];

  const iconSize = {
    sm: 12,
    md: 14,
    lg: 16,
  }[size];

  return (
    <span className={`role-badge ${className}`} style={style}>
      <IconComponent size={iconSize} aria-hidden="true" />
      <span>{ROLE_LABELS[role]}</span>
    </span>
  );
}

/**
 * Lista badge'y dla wielu ról
 */
export function RoleBadgeList({
  roles,
  size = 'md',
  className = '',
}: {
  roles: RoleName[];
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  if (roles.length === 0) return null;

  // Sortuj role według priorytetu (admin > moderator > user)
  const sortedRoles = [...roles].sort((a, b) => {
    const priority = { admin: 3, moderator: 2, user: 1 };
    return priority[b] - priority[a];
  });

  return (
    <div
      className={`role-badge-list ${className}`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        alignItems: 'center',
      }}
    >
      {sortedRoles.map((role) => (
        <RoleBadge key={role} role={role} size={size} />
      ))}
    </div>
  );
}

